import { FormEvent, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import merge from "lodash/merge";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { MultiLanguageWrapper, SelectOption } from "@iddataweb/idw-common-ui/types";
import { AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { LabelWidthTypes } from "@enums/label-width-types";
import { createMessagingTemplateReduxRequest } from "@redux-requests/messaging-templates";
import { DefaultModal } from "@containers/common/modals/default-modal/default-modal";
import { MessagingTemplateTypes } from "@enums/messaging-template-types";
import { MessagingTemplateMetadataForm } from "@containers/modals/add-messaging-template-modal/messaging-template-metadata-form/messaging-template-metadata-form";
import { MessagingTemplateVariablesForm } from "@containers/modals/add-messaging-template-modal/messaging-template-variables-form/messaging-template-variables-form";
import { convertDotSeparatedStringToJSONObject, convertJSONObjectToDotSeparatedString } from "@utils/string-util";
import { FormattedMessagingTemplate, MessagingTemplate } from "@idw-types/data/messaging-template";
import { MessagingTemplateContentForm } from "@containers/modals/add-messaging-template-modal/messaging-template-content-form/messaging-template-content-form";
import { getMessagingTemplateValidationErrors } from "@utils/validation/messaging-template/messaging-template-validation-util";
import { removeUnusedVariablesFromMessagingTemplateLanguageFiles } from "@utils/messaging-template-util";
import { IDWSession } from "@idw-types/data/idw-session";
import { getInitialMessagingTemplate } from "@utils/messaging-template/messaging-template-util";
import { messagingTemplateContentStringRegex } from "@constants/messaging-template-constants";

type AddMessagingTemplateModalProps = {
    session: IDWSession;
    onModalClose: () => void;
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
    availableMessagingTemplateTypeOptions: Array<SelectOption<MessagingTemplateTypes>>;
    isVericationServiceDetailsPage: boolean;
    serviceRelyingPartyId: string;
    serviceOrganizationId: string;
    addToReferenceData: boolean;
    onMessagingTemplateCreated?: (messagingTemplate: MessagingTemplate) => void;
};

export const AddMessagingTemplateModal = (props: AddMessagingTemplateModalProps) => {
    const dispatch = useDispatch();
    const {
        session,
        onModalClose,
        axnCompanies,
        axnRelyingParties,
        isVericationServiceDetailsPage,
        availableMessagingTemplateTypeOptions,
        onMessagingTemplateCreated,
        serviceRelyingPartyId,
        serviceOrganizationId,
        addToReferenceData
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [modalStep, setModalStep] = useState(0);

    const [selectedLanguage, setSelectedLanguage] = useState<LocaleTypes>(LocaleTypes.ENGLISH);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [state, setState] = useState<FormattedMessagingTemplate>(getInitialMessagingTemplate(
        axnCompanies,
        axnRelyingParties,
        isVericationServiceDetailsPage,
        session,
        availableMessagingTemplateTypeOptions
    ));

    useEffect(() => {
        const matches = state.template.match(messagingTemplateContentStringRegex);

        const languageFiles: Partial<MultiLanguageWrapper<{ [key: string]: any }>> = {};
        //initialize language content for each available language
        (Object.keys(state.languageFiles) as Array<LocaleTypes>).forEach(locale => {
            languageFiles[locale] = state.languageFiles[locale] || {};
        });

        if (matches) {
            //iterate over variables
            matches.forEach(match => {
                //@ts-ignore
                const splitVar = match.split(/!(.*)/s);
                if (splitVar && splitVar.length > 1) {
                    //split the variable names and values
                    const variableName = splitVar[0].substring(3, splitVar[0].length - 1);
                    const variableValue = splitVar[1].substring(1, splitVar[1].length - 2);

                    (Object.keys(state.languageFiles) as Array<LocaleTypes>).forEach(locale => {
                        //merge this variable name and value into the object
                        const flattenedLanguageFile = convertJSONObjectToDotSeparatedString(state.languageFiles[locale]);
                        const previousValue = flattenedLanguageFile[variableName] || null;

                        if (!previousValue) {
                            const newJsonObject = convertDotSeparatedStringToJSONObject(variableName, variableValue);
                            languageFiles[locale] = merge({}, languageFiles[locale], newJsonObject);
                        } else {
                            const previousJsonObject = convertDotSeparatedStringToJSONObject(variableName, previousValue);
                            languageFiles[locale] = merge({}, languageFiles[locale], previousJsonObject)
                        }
                    });
                }
            });
        }

        setState({
            ...state,
            languageFiles
        });
    }, [state.template]);

    const handleSubmitOnStep0 = (event: FormEvent) => {
        event.preventDefault();
        setModalStep(1);
    };

    const handleSubmitOnStep1 = (event: FormEvent) => {
        event.preventDefault();
        setModalStep(2);
    };

    const handleSubmitOnStep2 = async (event: FormEvent) => {
        event.preventDefault();
        setRequestInProgress(true);

        const newLanguageFiles = removeUnusedVariablesFromMessagingTemplateLanguageFiles(
            state.type,
            state.template,
            state.languageFiles
        );

        const newStringifiedLanguageFiles: Partial<MultiLanguageWrapper<string>> = {};

        (Object.keys(newLanguageFiles) as Array<LocaleTypes>).forEach((key => {
            newStringifiedLanguageFiles[key] = JSON.stringify(newLanguageFiles[key]);
        }));

        const newMessagingTemplate = await createMessagingTemplateReduxRequest(dispatch)(
            state.template,
            state.name,
            state.company?.id,
            state.relyingParty?.id,
            state.type,
            newStringifiedLanguageFiles,
            axnCompanies,
            axnRelyingParties,
            addToReferenceData
        );
        if (newMessagingTemplate) {
            if (onMessagingTemplateCreated) {
                onMessagingTemplateCreated(newMessagingTemplate);
            }
            onModalClose();
        } else {
            setRequestInProgress(false);
        }
    }

    const handleBackClickedOnStep1 = () => {
        setModalStep(0);
    };

    const handleBackClickedOnStep2 = () => {
        setModalStep(1);
    };

    const validationErrors = useMemo(() => {
        return getMessagingTemplateValidationErrors(state);
    }, [
        state.name, state.languageFiles, state.type,
        state.template, state.templateAvailabilityLevel,
        state.company, state.relyingParty
    ]);

    return (
        <DefaultModal
            automationId="add-messaging-template-modal"
            ariaLabelledBy="add-messaging-template-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={modalStep > 0}
        >
            {modalStep === 0 && (
                <MessagingTemplateMetadataForm
                    session={session}
                    axnCompanies={axnCompanies}
                    axnRelyingParties={axnRelyingParties}
                    validationErrors={validationErrors.generalInfo}
                    availableMessagingTemplateTypeOptions={availableMessagingTemplateTypeOptions}
                    isVericationServiceDetailsPage={isVericationServiceDetailsPage}
                    serviceOrganizationId={serviceOrganizationId}
                    serviceRelyingPartyId={serviceRelyingPartyId}
                    handleSubmit={handleSubmitOnStep0}
                    setSelectedLanguage={setSelectedLanguage}
                    labelWidth={labelWidth}
                    onModalClose={onModalClose}
                    setState={setState}
                    state={state}
                    requestInProgress={requestInProgress}
                />
            )}
            {modalStep === 1 && (
                <MessagingTemplateContentForm
                    state={state}
                    requestInProgress={requestInProgress}
                    selectedLanguage={selectedLanguage}
                    validationErrors={validationErrors.content}
                    setSelectedLanguage={setSelectedLanguage}
                    setState={setState}
                    onBackClicked={handleBackClickedOnStep1}
                    handleSubmit={handleSubmitOnStep1}
                />
            )}
            {modalStep === 2 && (
                <MessagingTemplateVariablesForm
                    state={state}
                    requestInProgress={requestInProgress}
                    selectedLanguage={selectedLanguage}
                    validationErrors={validationErrors.variables}
                    setSelectedLanguage={setSelectedLanguage}
                    setState={setState}
                    onBackClicked={handleBackClickedOnStep2}
                    handleSubmit={handleSubmitOnStep2}
                />
            )}
        </DefaultModal>
    );
};